import {
  ACTION_LIST_ALL_SCOPE_GRANTS,
  ACTION_READ_ANY_GRANTS,
  GRANTS,
  type GrantCode,
} from '../../auth/grants'
import { ROLE_IDS, type RoleId } from '../../auth/roles'

/** Tek kaynak menü tanımı (anayasa: menü tek kaynaktan yönetilir). Mockup ile birebir. */
export interface MenuSubItem {
  /** i18n anahtarı — sayfa başlığı da bundan gelir (anayasa: başlık = menü adı). */
  labelKey: string
  /**
   * Breadcrumb'ta {@code labelKey} yerine kullanılacak i18n anahtarı (opsiyonel). Bir grup altında kısa adla
   * duran öğeler (ör. "Arşivlenenler › Toplantılar") breadcrumb'ta tek başına anlamsız kaldığında verilir;
   * ekranın tam adı (sayfa başlığı) gösterilir. Verilmezse breadcrumb {@code labelKey}'i kullanır.
   */
  breadcrumbLabelKey?: string
  /**
   * Rota yolu (İngilizce). **Grup öğelerinde (alt menüsü olanlar) bulunmaz** — grup yalnız açılıp
   * alt öğeleri gösterir, kendi sayfası yoktur.
   */
  path?: string
  /** İkon CSS sınıfı — kapalı menüde aktif modülün altları ikon+tooltip olarak gösterilir (`navic navic-*`). */
  icon: string
  /**
   * Bu ekranı görmek için gereken grant kodlarından **en az biri** (FR-016 — menü görünürlüğü;
   * page-grant-map.md). Verilmezse öğe her zaman görünür (henüz yetkiye bağlanmamış placeholder).
   * Asıl koruma backend'dedir (FR-009); bu yalnız UX.
   */
  viewGrants?: [GrantCode, ...GrantCode[]]
  /**
   * Bu öğeyi belirli **rollerde** elle gizler — rol <b>kimliğiyle</b> ({@code /me} → {@code roleIds}),
   * adıyla değil: rol adı analistçe değiştirilebilir ve ada bağlı bir kural o gün sessizce çalışmayı
   * bırakırdı (gerekçe: {@link ROLE_IDS}). Yalnız grant ile gizlenemeyen kozmetik istisnalar için (ör.
   * Genel Müdür hesabı yetkiyi Yönetici rolünden de aldığından grant kaldırılamıyor). Kullanıcının
   * rolleri arasında bunlardan <b>herhangi biri</b> varsa öğe süzülür (NavMenu). Asıl koruma
   * backend'dedir; bu yalnız UX.
   */
  hideForRoleIds?: [RoleId, ...RoleId[]]
  /**
   * Alt menü öğeleri (tek seviye iç içe — ör. "Arşivlenenler" → Toplantılar / Aksiyonlar; "Parametreler" →
   * Toplantı Grupları / Skor / Kayıt Tipi). Verilirse bu öğe bir **grup**tur: {@code path} taşımaz, tıklanınca
   * yalnız açılıp/kapanır; gezinme alt öğelerdedir. Çocuklar da kendi {@code viewGrants}'ları ile süzülür
   * (NavMenu); hiç görünür çocuğu kalmayan grup gizlenir.
   */
  children?: MenuSubItem[]
  /**
   * <b>Yalnız tek görünür çocuk kaldığında</b> grubu düzleştirir: aç/kapa oklu satır yerine doğrudan
   * o çocuğa giden bir yaprak çizilir, ama etiket <b>grubun</b> adı olarak kalır (NavMenu).
   *
   * <p><b>Neden opt-in, neden genel kural DEĞİL:</b> grup adları çoğunlukla <b>jeneriktir</b>
   * ("Parametreler", "Görevler", "Arşivlenenler") ve tek başlarına nereye gittiklerini anlatmazlar —
   * projede {@code breadcrumbLabelKey}'in var olma sebebi tam olarak budur. Genel bir kural,
   * tek yetkili bir kullanıcıya "Parametreler" yazan ve habersizce "Bilet Ret Gerekçesi"ne giden bir
   * satır gösterirdi (bağımsız code-review bulgusu, 28.09.2026). Bu yüzden davranış, grubun adı
   * gidilecek yeri <b>zaten anlatan</b> modüllerde <b>açıkça</b> işaretlenir.
   *
   * <p>Bugün tek kullanıcısı Zaman Portföyü'dür: grup adı modül adıyla aynıdır ("Zaman Portföyü") ve
   * Genel Müdür'ün yalnız Rapor'u görmesi hâlinde menüde düz "Zaman Portföyü" görmesi <b>istenen</b>
   * davranıştır (kullanıcı isteği 28.09.2026).
   */
  flattenWhenSingle?: true
}

export interface MenuModule {
  key: string
  /** Modül başlığı i18n anahtarı */
  labelKey: string
  /**
   * **Başlıksız modül** (2026-09-18 kullanıcı isteği). `true` ise modülün adı hiçbir yerde çizilmez:
   * sol menüde öğeleri doğrudan kök seviyede görünür (açılıp kapanan bir başlık yoktur) ve üst
   * çubuktaki breadcrumb modül adımını atlar.
   *
   * <p>Modül kaydı yine de korunur — `key`, `basePath` ve sıra aktif ekran tespitinde ({@link
   * useActiveMenu}) ve rota kilidinde kullanılıyor; yalnız **görünürlüğü** kalkar. Kayıt silinseydi
   * bu üç mekanizmanın da yeniden kurulması gerekirdi.
   */
  flat?: boolean
  /** İkon CSS sınıfı — Figma modül ikonları CSS mask ile (`navic navic-*`, bkz. AppLayout.css). */
  icon: string
  /** Aktiflik tespiti için rota öneki (ör. /minutes) */
  basePath: string
  /** En az bir alt öğe (non-empty) — `items[0]` erişimi tip düzeyinde güvenli. */
  items: [MenuSubItem, ...MenuSubItem[]]
}

export const MODULES: MenuModule[] = [
  {
    // Admin yönetim modülü (015): Kullanıcı-Rol-Yetki + Sistem Parametreleri + Referans. Menüde en üstte.
    // Not: bilet lookup ekranları da /admin/* yolundadır ama bilet modülüne aittir; aktif modül tespiti
    // öğe-yolu eşleşmesiyle (useActiveMenu) yapıldığından çakışmaz (basePath öneki tek başına kullanılmaz).
    key: 'admin',
    labelKey: 'nav.admin.label',
    icon: 'navic navic-admin',
    basePath: '/admin',
    // 126 (26.10.2026): yönetim öğeleri `_MANAGE` YANINDA salt-okunur türevi (`_READONLY`) de kabul eder —
    // Kısıtlı Yetkili asistan, asilin gördüğü yönetim ekranlarını salt okunur görür (türevi sunucu
    // türetir, bkz. `GRANTS` yorumu). Vekalet (`USER_PROXY_MANAGE`) bilinçle hariç: Tam Yetkide bile kapalı.
    items: [
      {
        labelKey: 'nav.admin.users',
        path: '/admin/users',
        icon: 'navic navic-users',
        viewGrants: [GRANTS.USER_MANAGE, GRANTS.USER_MANAGE_READONLY],
      },
      {
        // Grup Yönetimi (122) — Kullanıcı Yönetimi'nin hemen altında, aynı yetkiyle.
        labelKey: 'nav.admin.groups',
        path: '/admin/groups',
        icon: 'navic navic-list',
        viewGrants: [GRANTS.USER_MANAGE, GRANTS.USER_MANAGE_READONLY],
      },
      {
        labelKey: 'nav.admin.roles',
        path: '/admin/roles',
        icon: 'navic navic-user-cog',
        viewGrants: [GRANTS.ROLE_MANAGE, GRANTS.ROLE_MANAGE_READONLY],
      },
      {
        labelKey: 'nav.admin.grants',
        path: '/admin/grants',
        icon: 'navic navic-shield',
        viewGrants: [GRANTS.GRANT_MANAGE, GRANTS.GRANT_MANAGE_READONLY],
      },
      {
        labelKey: 'nav.admin.systemParameters',
        path: '/admin/system-parameters',
        icon: 'navic navic-sliders',
        viewGrants: [GRANTS.SYSTEM_PARAMETER_MANAGE, GRANTS.SYSTEM_PARAMETER_MANAGE_READONLY],
      },
      {
        labelKey: 'nav.admin.referans',
        path: '/admin/referans',
        icon: 'navic navic-list',
        viewGrants: [GRANTS.REFERANS_MANAGE, GRANTS.REFERANS_MANAGE_READONLY],
      },
      // 045: Admin modülünün (o günkü) EN SON öğesi (kullanıcı isteği: "ana menünün en altına").
      {
        labelKey: 'nav.admin.operationCodes',
        path: '/admin/operation-codes',
        icon: 'navic navic-tag',
        viewGrants: [GRANTS.OPERATION_CODE_MANAGE, GRANTS.OPERATION_CODE_MANAGE_READONLY],
      },
      // 059: Admin modülünün en altına iki öğe (kullanıcı isteği: "2 menü şeklinde").
      // Sıra bağlayıcıdır: önce şablon, sonra onun yer tutucularını tanımlayan parametre kataloğu.
      {
        labelKey: 'nav.admin.notificationTemplates',
        path: '/admin/notification-templates',
        icon: 'navic navic-list',
        viewGrants: [GRANTS.NOTIFICATION_TEMPLATE_MANAGE, GRANTS.NOTIFICATION_TEMPLATE_MANAGE_READONLY],
      },
      {
        labelKey: 'nav.admin.templateParameters',
        path: '/admin/template-parameters',
        icon: 'navic navic-sliders',
        viewGrants: [GRANTS.TEMPLATE_PARAMETER_MANAGE, GRANTS.TEMPLATE_PARAMETER_MANAGE_READONLY],
      },
      // 078 "Bildirimler": Admin modülünün en son öğesi. Şablon (059) "ne gönderilecek"i tanımlar; bu ekran
      // "ne gönderildi"yi gösterir → doğal olarak o iki öğenin ardında durur.
      {
        labelKey: 'nav.admin.sentEmails',
        path: '/admin/sent-emails',
        icon: 'navic navic-mail',
        viewGrants: [GRANTS.SENT_EMAIL_VIEW],
      },
      // 090 "Vekalet": Admin modülünün en son öğesi (kullanıcı isteği: "Admin menüsü altına").
      // Yetki devrini yönettiği için doğal komşuları Kullanıcı/Rol/Yetki öğeleridir; yine de sıra
      // bozulmasın diye repo kuralına uyup EN ALTA eklendi (045/059/078 ile aynı yaklaşım).
      {
        labelKey: 'nav.admin.userProxies',
        path: '/admin/user-proxies',
        icon: 'navic navic-user-cog',
        viewGrants: [GRANTS.USER_PROXY_MANAGE],
      },
      {
        // 2026-09-16 kullanıcı isteği: Tutanak Takip modülündeki "Parametreler" grubu OLDUĞU GİBİ
        // Admin modülünün altına taşındı (grup olarak; öğeler tek tek dağıtılmadı) ve repo kuralına
        // uyup EN ALTA eklendi (045/059/078/090 ile aynı yaklaşım).
        //
        // Rota yolları da taşındı: `/minutes/*` → `/admin/parameters/*` (kullanıcı kararı — adres
        // menüdeki yeri yansıtsın). Eski yollar `routes.tsx`'te kalıcı yönlendirme olarak korunur,
        // yoksa kayıtlı bağlantılar ve yer imleri 404'e düşerdi.
        labelKey: 'nav.admin.parameters',
        icon: 'navic navic-sliders',
        children: [
          {
            labelKey: 'nav.tutanak.meetingGroups',
            path: '/admin/parameters/meeting-groups',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.MEETING_GROUP_MANAGE, GRANTS.MEETING_GROUP_MANAGE_READONLY],
          },
          {
            labelKey: 'nav.tutanak.meetingDecisionTypes',
            path: '/admin/parameters/meeting-decision-types',
            icon: 'navic navic-tag',
            viewGrants: [GRANTS.MEETING_DECISION_TYPE_MANAGE, GRANTS.MEETING_DECISION_TYPE_MANAGE_READONLY],
          },
          // 056: Görünen kayıt numaralarının ön ekleri (REF_PREFIX) — salt liste + ad/ön ek
          // güncelleme; ekleme/silme/durum yok.
          {
            labelKey: 'nav.tutanak.prefixes',
            path: '/admin/parameters/prefixes',
            icon: 'navic navic-tag',
            viewGrants: [GRANTS.PREFIX_MANAGE, GRANTS.PREFIX_MANAGE_READONLY],
          },
          // 22.09.2026 (kullanıcı isteği): Toplantı Türü (REF_MEETING_TYPE) — tam CRUD. Grubun EN
          // ALTINA eklendi; repo kuralı yeni öğeyi sona koyar, mevcut sıra bozulmaz.
          {
            labelKey: 'nav.admin.meetingTypes',
            path: '/admin/parameters/meeting-types',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.MEETING_TYPE_MANAGE, GRANTS.MEETING_TYPE_MANAGE_READONLY],
          },
          // 23.09.2026 (kullanıcı isteği): Tatil Günü (REF_HOLIDAY_DAY) — tam CRUD. Yine EN ALTA.
          {
            labelKey: 'nav.admin.holidayDays',
            path: '/admin/parameters/holiday-days',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.HOLIDAY_DAY_MANAGE, GRANTS.HOLIDAY_DAY_MANAGE_READONLY],
          },
          // 30.09.2026 (kullanıcı isteği): Liste Yönetimi (MANAGER_LIST + MANAGER_LIST_PEOPLE) —
          // adlandırılmış kişi listeleri; ekle/düzenle/sil + kişi ekleme-çıkarma. Yine EN ALTA.
          {
            labelKey: 'nav.admin.managerLists',
            path: '/admin/parameters/manager-lists',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.MANAGER_LIST_MANAGE, GRANTS.MANAGER_LIST_MANAGE_READONLY],
          },
          // 127 (21.10.2026) — Stratejik Hedefler + Katkılar (REF_STRATEGIC_OBJECTIVE /
          // REF_CONTRIBUTION): görevlerde seçilen iki sıralanabilir katalog; tek ekran bileşeni, her biri
          // KENDİ yetkisine bağlı (biri diğerini açmaz; roller 1 · 3 · 4, kullanıcı kararı). Yine EN ALTA.
          {
            labelKey: 'nav.admin.strategicObjectives',
            path: '/admin/parameters/strategic-objectives',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.STRATEGIC_OBJECTIVE_MANAGE, GRANTS.STRATEGIC_OBJECTIVE_MANAGE_READONLY],
          },
          {
            labelKey: 'nav.admin.contributions',
            path: '/admin/parameters/contributions',
            icon: 'navic navic-list',
            viewGrants: [GRANTS.CONTRIBUTION_MANAGE, GRANTS.CONTRIBUTION_MANAGE_READONLY],
          },
        ],
      },
    ],
  },
  {
    key: 'tutanak',
    labelKey: 'nav.tutanak.label',
    // 2026-09-18 kullanıcı isteği: "Tutanak Takip" ana başlığı kaldırıldı; altındaki ekranlar sol
    // menüde doğrudan görünür. Etiket anahtarı SİLİNMEDİ: modül kaydı (key/basePath) aktif ekran
    // tespiti ve rota kilidi için duruyor, `labelKey` de o kaydın zorunlu alanı.
    flat: true,
    icon: 'navic navic-tutanak',
    basePath: '/minutes',
    // Sıra kullanıcı isteğiyle sabittir (son düzen: 2026-09-16 maili — Toplantı Tutanakları ·
    // Görevler · Prensip Kararları · Arşivlenenler; Onaylarım 2026-11-04'te Görevler ile Prensip
    // Kararları arasına girdi). Modülün TÜM yaprakları kendi görüntüleme
    // yetkilerine bağlıdır; ekranı henüz gerçeklenmemiş olan (Toplantı Yönetimi) için routes.tsx
    // otomatik PlaceholderPage üretir — yetkisi olmayan onu da göremez.
    // Not: "Dashboard" öğesi (placeholder, /minutes/dashboard) kullanıcı isteğiyle menüden kaldırıldı.
    items: [
      {
        // Grup (2026-09-16 kullanıcı isteği): "Toplantı Tutanakları" — Toplantılar + Tutanak Oluştur
        // tek başlık altında toplandı. Grubun kendi sayfası yoktur; gezinme çocuklardadır.
        labelKey: 'nav.tutanak.meetingMinutes',
        icon: 'navic navic-tutanak',
        children: [
          {
            labelKey: 'nav.tutanak.meet',
            path: '/minutes/meeting-minutes/meetings',
            icon: 'navic navic-users',
            // Ucun okuma kapısı HAS_MEETING_GROUP_READ = VIEW **veya** MANAGE (Grants.java:261) — ikisi de
            // yazılır. Rota kilidi bu listeden türetildiği için (routes.tsx → MENU_VIEW_GRANTS), tek grant
            // bırakmak yalnız MANAGE'i olan (ayrıcalıklı) kullanıcıyı sunucunun izin verdiği ekrandan
            // kilitlerdi.
            viewGrants: [GRANTS.MEETING_GROUP_VIEW, GRANTS.MEETING_GROUP_MANAGE],
          },
          {
            labelKey: 'nav.tutanak.new',
            path: '/minutes/meeting-minutes/create',
            icon: 'navic navic-file-plus',
            viewGrants: [GRANTS.MINUTES_CREATE],
          },
        ],
      },
      {
        // Grup: kendi sayfası yok; dört aksiyon kapsamı ARTIK ayrı alt sayfadır (eski sekmeli tek ekran
        // yerine — 2026-08-11 kullanıcı kararı). Çocuklar kendi görüntüleme yetkileriyle süzülür (NavMenu);
        // görünür çocuğu kalmayan grup gizlenir. Kapsam sırası kullanıcı isteğiyle sabittir.
        labelKey: 'nav.tutanak.actions',
        icon: 'navic navic-check-square',
        children: [
          {
            // 2026-09-16 kullanıcı isteği: Görevler grubunun İLK öğesi. Yol, grubun diğer
            // öğeleriyle aynı `/minutes/actions/*` önekini taşır — adres menüdeki yeri yansıtsın.
            // Ekranın içeriği henüz tasarlanmadı → rota `routes.tsx`'te IMPLEMENTED'a girmez,
            // otomatik PlaceholderPage gösterilir; menü kaydı + yetki + rota kilidi bugün çalışır.
            // Görünürlük TEK yetkiye bağlıdır (MEETING_MANAGEMENT_VIEW): Süper Admin · Özel Kalem
            // Personeli · Özel Kalem Müdürü rollerine verilir (seed: specs/_maintenance/).
            labelKey: 'nav.tutanak.meetingManagement',
            path: '/minutes/actions/meeting-management',
            // Kardeşlerinden AYRI bir ikon: daraltılmış menüde grup çocukları düzleştirilip yan yana
            // ikon olarak dizilir; "Toplantılar" ile aynı ikon seçilseydi ayrım yalnız tooltip'te kalırdı.
            icon: 'navic navic-gauge',
            viewGrants: [GRANTS.MEETING_MANAGEMENT_VIEW],
          },
          {
            // Etiketler alt sayfa başlıklarıyla aynı i18n anahtarını paylaşır (actions.tabs.*) → başlık = menü adı.
            labelKey: 'actions.tabs.all',
            path: '/minutes/actions/all',
            icon: 'navic navic-check-square',
            // Sınırlı yetki (Genel Müdür, 06.10.2026) de açar; daraltmayı sunucu yapar.
            viewGrants: [...ACTION_LIST_ALL_SCOPE_GRANTS],
          },
          {
            labelKey: 'actions.tabs.created',
            path: '/minutes/actions/created',
            icon: 'navic navic-check-square',
            viewGrants: [GRANTS.ACTION_LIST_OWN_VIEW],
          },
          {
            labelKey: 'actions.tabs.assigned',
            path: '/minutes/actions/assigned',
            icon: 'navic navic-check-square',
            viewGrants: [GRANTS.ACTION_LIST_ASSIGNED_VIEW],
          },
          {
            // "Takip Ettiklerim" Genel Müdür rolünde GİZLİ (kullanıcı kuralı 2026-08-06). GM hesabı
            // ayrıca Yönetici rolü taşıyıp yetkiyi aldığından grant ile gizlenemez → rol KİMLİĞİNE göre
            // elle gizlenir. ("Bana Atananlar" 2026-08-11'de GM'ye görünür yapıldı — yalnız bu öğe gizli.)
            // Yönetim Kurulu Başkanı'nda da aynı şekilde gizli (kullanıcı kuralı 2026-10-14).
            labelKey: 'actions.tabs.followed',
            path: '/minutes/actions/followed',
            icon: 'navic navic-check-square',
            viewGrants: [GRANTS.ACTION_LIST_FOLLOWED_VIEW],
            hideForRoleIds: [ROLE_IDS.GENEL_MUDUR, ROLE_IDS.YONETIM_KURULU_BASKANI],
          },
          {
            // 087 — Hedef teslim tarihi güncelleme talepleri. 2026-09-16'da modül kökünden Görevler grubunun ALTINA
            // taşındı (kullanıcı isteği); yol da grup kardeşleriyle aynı öneke alındı
            // (`/minutes/deadline-requests` → `/minutes/actions/deadline-requests`). Yetki aynen korundu.
            //
            // Görünürlük, ucun HAS_ACTION_READ_ANY korumasını BİREBİR aynalar (tek kaynak:
            // ACTION_READ_ANY_GRANTS): aksiyonu hiç göremeyen kullanıcının talebi de olamaz.
            // `viewGrants` verilmezse öğe herkese görünür ama uç 403 döner → kırık menü öğesi.
            labelKey: 'nav.tutanak.deadlineRequests',
            path: '/minutes/actions/deadline-requests',
            // Onay bekleyen kayıtların toplandığı ekran → "gelen kutusu" ikonu (mevcut set; yeni SVG eklenmedi).
            icon: 'navic navic-inbox',
            viewGrants: [...ACTION_READ_ANY_GRANTS],
          },
        ],
      },
      {
        // 131 — Onaylarım: hedef teslim tarihi talepleri ile Onay Bekleniyor görevlerin kapatılması
        // tek ekranda, iki sekmede. Talep ekranı ve Toplantı Yönetimi DEĞİŞMEDEN kalır.
        //
        // Yeri kullanıcı isteğidir: kök seviyede, Görevler grubu ile Prensip Kararları ARASI
        // (2026-11-04 — önce Görevler grubunun içindeydi). Yol da grubun `/minutes/actions/*` önekinden
        // çıktı — adres menüdeki yeri yansıtsın; eski adres MOVED ile yönlenir (routes.tsx).
        //
        // Görünürlük TEK yetkiye bağlıdır (MY_APPROVALS_VIEW, kullanıcı kararı 2026-11-03); rota
        // kilidi bundan türer. Ekranın İÇİNDEKİ sekmeler ise kendi işlemlerinin mevcut yetkisine
        // bakar (talepler → ACTION_READ_ANY_GRANTS, görev kapatma → MEETING_MANAGEMENT_VIEW) — uçlar
        // o kodlarla korunduğu için sekme görünürlüğü onlarla aynı kalmak zorundadır.
        labelKey: 'nav.tutanak.approvals',
        path: '/minutes/approvals',
        icon: 'navic navic-check-square',
        viewGrants: [GRANTS.MY_APPROVALS_VIEW],
      },
      {
        labelKey: 'nav.tutanak.principleDecisions',
        path: '/minutes/principle-decisions',
        icon: 'navic navic-gavel',
        viewGrants: [GRANTS.PRINCIPLE_DECISION_VIEW],
      },
      {
        // Notlarım (113) — kişisel yapılacak-iş listeleri. Konum kullanıcı isteğidir: Prensip
        // Kararları'nın hemen ALTI. Yetki sekiz rolün hepsinde olduğundan öğe herkese görünür;
        // kullanıcının NE göreceği (kendi + kendisiyle paylaşılan listeler) kayıt düzeyinde,
        // sunucuda belirlenir — menü yetkisi bunu kısıtlamaz.
        labelKey: 'nav.tutanak.todo',
        path: '/minutes/todo-lists',
        icon: 'navic navic-check-square',
        viewGrants: [GRANTS.TODO_LIST_VIEW],
      },
      {
        // Ajanda (125) — 15.10.2026'dan beri yetkiye bağlı (ilk kurulumda tüm rollerde). Rota kilidi
        // bu listeden türer; detay rotası (`/minutes/agenda/:id`) ise bilinçli olarak kilitsizdir —
        // yetkisi olmayan davetli de davet edildiği toplantıyı açabilmeli (routes.tsx).
        labelKey: 'nav.tutanak.agenda',
        path: '/minutes/agenda',
        icon: 'navic navic-calendar',
        viewGrants: [GRANTS.AGENDA_VIEW],
      },
      {
        // Grup: kendi sayfası yok; Toplantılar + Aksiyonlar alt menüleri (arşiv).
        labelKey: 'nav.tutanak.archived',
        icon: 'navic navic-inbox',
        children: [
          {
            labelKey: 'nav.tutanak.archivedMeetings',
            // Breadcrumb'ta "Toplantılar" değil "Arşivlenen Toplantılar" (= sayfa başlığı) görünür.
            breadcrumbLabelKey: 'meetings.archived.title',
            path: '/minutes/archived/meetings',
            viewGrants: [GRANTS.MEETING_ARCHIVE_VIEW],
            icon: 'navic navic-users',
          },
          {
            labelKey: 'nav.tutanak.archivedActions',
            breadcrumbLabelKey: 'actions.archived.title',
            path: '/minutes/archived/actions',
            viewGrants: [GRANTS.ACTION_ARCHIVE_VIEW],
            icon: 'navic navic-check-square',
          },
        ],
      },
      // "Tutanak Oluştur" yukarıdaki "Toplantı Tutanakları" grubuna, "Parametreler" grubu ise Admin
      // modülüne taşındı (2026-09-16 kullanıcı isteği) — ikisi de bu modülün kökünde artık yoktur.
    ],
  },
  {
    // Zaman Portfoyu (23.09.2026 kullanici istegi): "Ana menuye 'Zaman Portfoyu Veri Girisi' menusu
    // ekleyelim, yerini isterlerse degistiriz." Yeri 23.09.2026'da kullanici istegiyle Etkinlik
    // Takibi'nin USTUNE alindi. `flat` ile modul basligi cizilmez; asagidaki GRUP dogrudan kok
    // seviyede gorunur — kullanicinin istedigi "ana menu" yerlesimi budur.
    key: 'zamanPortfoyu',
    labelKey: 'nav.timePortfolio.label',
    flat: true,
    // Ikon seti sabittir (public/nav-icons/nav/*.svg) ve saat ikonu YOK; en yakin anlam "gosterge"
    // (gauge) — yeni bir SVG uydurmak yerine mevcut setten secildi.
    icon: 'navic navic-gauge',
    basePath: '/time-portfolio',
    items: [
      {
        // 28.09.2026 kullanici istegi: modul iki ekranli oldu, bu yuzden tek yapraktan GRUBA gecti.
        //
        // Grubun iki cocugu AYRI yetkilere baglidir ve kume kasitli olarak ayrisir:
        //   · Veri Girisi → TIME_PORTFOLIO_MANAGE  (roller 1 · 3 · 4 · 8 GM Asistani)
        //   · Rapor       → TIME_PORTFOLIO_REPORT_VIEW (roller 1 · 3 · 4 · 5 Genel Mudur)
        // Genel Mudur yalniz RAPORU gorur (veri girisi tam CRUD'dur, ona verilmedi); GM Asistani ise
        // tersine yalniz VERI GIRISINI gorur. Sonuc: GM'de grubun tek gorunur cocugu kalir ve
        // NavMenu onu grup adiyla ("Zaman Portfoyu") duz bir ogeye indirger — istenen davranis budur.
        labelKey: 'nav.timePortfolio.label',
        icon: 'navic navic-gauge',
        // Grup adi modul adiyla aynidir ve gidilecek yeri anlatir → tek cocuk kalinca duzlesebilir.
        flattenWhenSingle: true,
        children: [
          {
            labelKey: 'nav.timePortfolio.entry',
            // Ikon seti sabittir (public/nav-icons/nav/*.svg): veri girisi = form, rapor = liste.
            icon: 'navic navic-form',
            path: '/time-portfolio/entry',
            viewGrants: [GRANTS.TIME_PORTFOLIO_MANAGE],
            // Menude "Veri Girisi" yeterli (grup zaten "Zaman Portfoyu" der), ama ust seritte tek
            // basina baglamsiz kalirdi — ayni kalip "Arsivlenenler" grubunda da kullaniliyor.
            breadcrumbLabelKey: 'timePortfolio.breadcrumb.entry',
          },
          {
            labelKey: 'nav.timePortfolio.report',
            icon: 'navic navic-list',
            path: '/time-portfolio/report',
            viewGrants: [GRANTS.TIME_PORTFOLIO_REPORT_VIEW],
            breadcrumbLabelKey: 'timePortfolio.breadcrumb.report',
          },
        ],
      },
    ],
  },
  {
    key: 'etkinlik',
    labelKey: 'nav.etkinlik.label',
    icon: 'navic navic-etkinlik',
    basePath: '/events',
    items: [
      {
        labelKey: 'nav.etkinlik.list',
        path: '/events/list',
        icon: 'navic navic-list',
        viewGrants: [GRANTS.ACTIVITY_VIEW],
      },
      {
        labelKey: 'nav.etkinlik.form',
        path: '/events/new',
        icon: 'navic navic-form',
        viewGrants: [GRANTS.ACTIVITY_CREATE],
      },
      // Yönetici Özeti sol menüden kaldırıldı (029): ekrana yalnız Etkinlik Listesi'ndeki "İncele" ile
      // ulaşılır (?id= ile). Rota routes.tsx'te (IMPLEMENTED) açıkça korunur.
      {
        // Grup: kendi sayfası yok; etkinlik parametre (lookup) ekranları burada toplanır — Paydaş ve Katılımcı,
        // Etkinlik Türü, Davet Türü, Ülke ve Şehir, Değerlendirme Skoru, Değerlendirme Kriterleri. Çocuklar
        // kendi yetkileriyle süzülür (NavMenu); hiç görünür çocuğu kalmayan grup gizlenir.
        labelKey: 'nav.etkinlik.parameters',
        icon: 'navic navic-sliders',
        children: [
          {
            labelKey: 'nav.etkinlik.stakeholders',
            path: '/events/stakeholders',
            icon: 'navic navic-users',
            viewGrants: [GRANTS.ACTIVITY_STAKEHOLDER_MANAGE],
          },
          {
            labelKey: 'nav.etkinlik.eventTypes',
            path: '/events/event-types',
            icon: 'navic navic-tag',
            viewGrants: [GRANTS.ACTIVITY_EVENT_TYPE_MANAGE],
          },
          {
            labelKey: 'nav.etkinlik.invitationTypes',
            path: '/events/invitation-types',
            icon: 'navic navic-mail',
            viewGrants: [GRANTS.ACTIVITY_INVITATION_TYPE_MANAGE],
          },
          {
            labelKey: 'nav.etkinlik.countries',
            path: '/events/countries',
            icon: 'navic navic-globe',
            viewGrants: [GRANTS.COUNTRY_MANAGE],
          },
          {
            labelKey: 'nav.etkinlik.criteriaScoreRange',
            path: '/events/criteria-score-ranges',
            icon: 'navic navic-gauge',
            viewGrants: [GRANTS.CRITERIA_SCORE_RANGE_MANAGE],
          },
          {
            labelKey: 'nav.etkinlik.criteriaScores',
            path: '/events/criteria-scores',
            icon: 'navic navic-check-square',
            viewGrants: [GRANTS.ACTIVITY_CRITERIA_SCORE_MANAGE],
          },
        ],
      },
    ],
  },
  // Bildirim merkezi (058) menüden KALDIRILDI: ekrana üst çubuktaki zil ikonundan (panel →
  // "Tümünü Gör") ulaşılıyor; menüde ikinci bir giriş tutmak aynı yola iki kapı açıyordu.
  // `/notifications` rotası `routes.tsx` içinde `IMPLEMENTED` altında kayıtlı olduğundan menü
  // kaydından bağımsız çalışır (MODULE_ITEMS yalnız placeholder rotaları üretir).
  {
    key: 'bilet',
    labelKey: 'nav.bilet.label',
    icon: 'navic navic-bilet',
    basePath: '/tickets',
    items: [
      {
        labelKey: 'nav.bilet.list',
        path: '/tickets/list',
        icon: 'navic navic-inbox',
        // Ucun kapisi HAS_TICKET_REQUEST_VIEW_ANY = UC kod; menude ikisi yaziliydi ve ucuncusu
        // (_VIEW_OWN_OR_IDENTIFIER, 037 ile Ozel Kalem Personeli icin eklendi) eksikti. Sonuc:
        // 037'nin TAM DA BU ROL ICIN yazdigi ekran o rolden gizleniyordu; backend ise istegi kabul
        // ediyordu -> menude yok ama url'den (ve Yeni Talep sonrasi yonlendirmeyle) aciliyordu.
        // THY sizma testi BGVL-137888 bunu raporladi. Menu kumesi ucun kumesinden DAR kalamaz:
        // dar kalirsa hem bu celiski dogar hem de menuden tureyen rota kilidi (BGVL-137394) devreye
        // girdiginde o rol kendi ekranindan tamamen kilitlenirdi.
        viewGrants: [
          GRANTS.TICKET_REQUEST_VIEW,
          GRANTS.TICKET_REQUEST_VIEW_OWN,
          GRANTS.TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER,
        ],
      },
      {
        labelKey: 'nav.bilet.new',
        path: '/tickets/new',
        icon: 'navic navic-plus-circle',
        viewGrants: [GRANTS.TICKET_REQUEST_CREATE],
      },
      // Talep Detay/Onay sol menüden kaldırıldı; ekrana yalnız listedeki "İncele" ile ulaşılır
      // (rota routes.tsx'te açıkça korunur).
      // Yönetim (lookup) ekranları bilet akışına ait olduğundan bu modül altına alındı (014); bağımsız Admin
      // modülü kaldırıldı. i18n anahtarları (nav.admin.*) korunur; rota yolları /tickets/* altına taşındı (056).
      {
        // Grup: kendi sayfası yok; bilet parametre (lookup) ekranları burada toplanır — Bilet Talep Türü,
        // Bilet Ret Gerekçesi. Çocuklar kendi yetkileriyle süzülür (NavMenu); görünür çocuğu kalmayan grup gizlenir.
        labelKey: 'nav.bilet.parameters',
        icon: 'navic navic-sliders',
        children: [
          {
            labelKey: 'nav.admin.types',
            path: '/tickets/ticket-types',
            icon: 'navic navic-tag',
            viewGrants: [GRANTS.TICKET_REQUEST_TYPE_MANAGE],
          },
          {
            labelKey: 'nav.admin.rejectReasons',
            path: '/tickets/reject-reasons',
            icon: 'navic navic-x-circle',
            viewGrants: [GRANTS.TICKET_REJECT_REASON_MANAGE],
          },
        ],
      },
    ],
  },
]

/**
 * Düz **yaprak** öğe listesi (router için): grup öğeleri (children) yerine alt öğeleri gelir. Böylece her
 * kaydın bir {@code path}'i vardır ve routes.tsx tüm gezinilebilir yollar için (varsa) PlaceholderPage üretir.
 */
export const MODULE_ITEMS: MenuSubItem[] = MODULES.flatMap((m) =>
  m.items.flatMap((item) => item.children ?? [item]),
)

/** Anasayfa (landing) yolu — kök (`/`). Uygulama açılışı + ev ikonu + logo buraya yönlenir. */
export const HOME_PATH = '/'

/**
 * Menüde vurgulanacak varsayılan modül tespiti için referans yol (menü mantığı). Landing artık kök
 * Anasayfa'dır ({@link HOME_PATH}); bu sabit menü modülü eşleştirmesi için korunur.
 */
export const DEFAULT_PATH = '/tickets/list'
